import {
  Consumer,
  ProductType as ApiProductType,
  TaxRuleCustomerCustomerAddressInterface,
  TaxRuleEntityType as ApiTaxRuleEntityType,
  TaxRuleInterface,
  TaxRuleTaxRuleEntityInterface,
  TaxRuleType as ApiTaxRuleType,
  ReplaceTaxRuleRequestInterface,
} from '@vendasta/billing';
import { TaxRate, taxRateFromApi } from './tax-rate';

export enum TaxRuleConsumer {
  WHOLESALE = 'wholesale',
  RETAIL = 'retail',
}

export enum TaxRuleType {
  UNSET = 'unset',
  EXEMPTION = 'exemption',
}

export enum TaxRuleEntityType {
  UNSET = 'unset',
  CUSTOMER = 'customer',
  PRODUCT = 'product',
}

export enum ProductType {
  PRODUCT = 'product',
  PACKAGE = 'package',
}

export interface ProductIdentifier {
  sku?: string;
  type?: ProductType;
  groupId?: string; // aka market id
}

export interface ProductEntity {
  name?: string;
  identifier?: ProductIdentifier;
}

export interface CustomerEntity {
  name?: string;
  address?: CustomerAddress;
}

export interface CustomerAddress {
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

export interface TaxRuleEntity {
  customer?: CustomerEntity;
  product?: ProductEntity;
}

export interface TaxRule {
  id?: string;
  merchantId: string;
  entityId: string;
  entityType: TaxRuleEntityType;
  entity?: TaxRuleEntity;
  taxRuleType: TaxRuleType;
  taxRates?: TaxRate[];
  taxRateIds?: string[];
}

export interface ListTaxRulesFilter {
  merchantId: string;
  entityType: TaxRuleEntityType;
}

export function taxRuleFromApi(apiTaxRule: TaxRuleInterface): TaxRule {
  return {
    id: apiTaxRule.id || '',
    merchantId: apiTaxRule.merchantId || '',
    entityId: apiTaxRule.entityId || '',
    entityType: taxRuleEntityTypeFromApi(apiTaxRule.entityType),
    entity: taxRuleEntityFromApi(apiTaxRule?.entity),
    taxRuleType: apiTaxRule.type === ApiTaxRuleType.TAX_RULE_TYPE_EXEMPTION ? TaxRuleType.EXEMPTION : TaxRuleType.UNSET,
    taxRates: taxRatesFromApi(apiTaxRule.taxRates || []),
  };
}

function taxRuleEntityFromApi(apiTaxRuleEntity: TaxRuleTaxRuleEntityInterface | undefined): TaxRuleEntity {
  if (!apiTaxRuleEntity) {
    return {
      customer: undefined,
      product: undefined,
    };
  }
  return {
    customer: apiTaxRuleEntity.customer
      ? {
          name: apiTaxRuleEntity.customer.name || '',
          address: apiTaxRuleEntity.customer.address
            ? taxRuleAddressFromApi(apiTaxRuleEntity.customer.address)
            : undefined,
        }
      : undefined,
    product: apiTaxRuleEntity.product
      ? {
          name: apiTaxRuleEntity.product.name || '',
          identifier: apiTaxRuleEntity.product.identifier
            ? {
                sku: apiTaxRuleEntity.product.identifier.sku || '',
                type: apiTaxRuleEntity.product.identifier.type
                  ? taxRuleProductTypeFromApi(apiTaxRuleEntity.product.identifier.type)
                  : undefined,
                groupId: apiTaxRuleEntity.product.identifier.groupId || '',
              }
            : undefined,
        }
      : undefined,
  };
}

function taxRuleProductTypeFromApi(apiProductType: ApiProductType): ProductType {
  switch (apiProductType) {
    case ApiProductType.PRODUCT_TYPE_PACKAGE:
      return ProductType.PACKAGE;
    default: // various other product options can be handled by falling back to "Product"
      return ProductType.PRODUCT;
  }
}

function taxRatesFromApi(apiTaxRates: TaxRate[]): TaxRate[] {
  return apiTaxRates.map((apiTaxRate) => {
    return taxRateFromApi(apiTaxRate);
  });
}

function taxRuleAddressFromApi(apiAddress: TaxRuleCustomerCustomerAddressInterface): CustomerAddress {
  return apiAddress;
}

export function taxRuleEntityTypeFromApi(entityType: ApiTaxRuleEntityType | undefined): TaxRuleEntityType {
  switch (entityType) {
    case ApiTaxRuleEntityType.TAX_RULE_ENTITY_TYPE_CUSTOMER:
      return TaxRuleEntityType.CUSTOMER;
    case ApiTaxRuleEntityType.TAX_RULE_ENTITY_TYPE_PRODUCT:
      return TaxRuleEntityType.PRODUCT;
    default:
      return TaxRuleEntityType.UNSET;
  }
}

export function taxRuleToReplaceTaxRuleApi(taxRules: TaxRule): ReplaceTaxRuleRequestInterface {
  return {
    merchantId: taxRules.merchantId,
    entityId: taxRules.entityId,
    entityType: taxRuleEntityTypeToApi(taxRules.entityType),
    type:
      taxRules.taxRuleType === TaxRuleType.EXEMPTION
        ? ApiTaxRuleType.TAX_RULE_TYPE_EXEMPTION
        : ApiTaxRuleType.TAX_RULE_TYPE_UNSET,
    taxRateIds: taxRules.taxRateIds
      ? taxRules.taxRateIds
      : (taxRules.taxRates || []).map((taxRate) => taxRate.id || ''),
    consumer: Consumer.CONSUMER_RETAIL,
  };
}
export function taxRulesToReplaceTaxRuleApi(taxRules: TaxRule[]): ReplaceTaxRuleRequestInterface[] {
  return taxRules.map(taxRuleToReplaceTaxRuleApi);
}

export function taxRuleEntityTypeToApi(entityType: TaxRuleEntityType): ApiTaxRuleEntityType {
  switch (entityType) {
    case TaxRuleEntityType.CUSTOMER:
      return ApiTaxRuleEntityType.TAX_RULE_ENTITY_TYPE_CUSTOMER;
    case TaxRuleEntityType.PRODUCT:
      return ApiTaxRuleEntityType.TAX_RULE_ENTITY_TYPE_PRODUCT;
    default:
      return ApiTaxRuleEntityType.TAX_RULE_ENTITY_TYPE_UNSET;
  }
}
